export interface FirstAttemptStartTimes {
  /** Records when `requestId` started. */
  set(requestId: string, startedAt: number): void;
  /** Returns and forgets `requestId`'s start time, or undefined if not tracked. */
  take(requestId: string): number | undefined;
  /** Forgets everything. */
  clear(): void;
  /** Number of tracked requests. Exposed for tests. */
  readonly size: number;
}

/**
 * Bounded store of in-flight request start times, keyed by request id.
 *
 * Owns its own eviction so callers never think about it. Entries normally leave
 * via `take` when the request settles; the bounds exist for the ones that never
 * do (a torn-down thunk whose settle action never arrives). Operators keep a
 * single tab open for a whole shift, so an unbounded map here would leak for
 * hours.
 *
 * Eviction is TTL first, then oldest-first, and runs on `set`:
 *   - entries older than `ttlMs` are dropped, and
 *   - if that still leaves no room, the oldest entries go until it does.
 *
 * Dropping oldest-first matters: the alternative of clearing wholesale would
 * blank the timing for every in-flight request at exactly the moment a client
 * is struggling enough to have hundreds of them.
 *
 * Both rules lean on `Map` preserving insertion order, which is start order
 * because callers pass `Date.now()`. A backwards clock step (NTP) can therefore
 * end the TTL sweep early and strand a few stale entries; the size cap still
 * bounds them, so this is left uncorrected rather than paying for a sort.
 */
export function createFirstAttemptStartTimes(options: {
  maxEntries: number;
  ttlMs: number;
}): FirstAttemptStartTimes {
  const { maxEntries, ttlMs } = options;
  const startTimes = new Map<string, number>();

  function evict(now: number): void {
    for (const [requestId, startedAt] of startTimes) {
      // Oldest first, so the first live entry ends the sweep.
      if (now - startedAt <= ttlMs) break;
      startTimes.delete(requestId);
    }
    while (startTimes.size >= maxEntries) {
      const oldest = startTimes.keys().next();
      if (oldest.done) break;
      startTimes.delete(oldest.value);
    }
  }

  return {
    set(requestId, startedAt) {
      evict(startedAt);
      startTimes.set(requestId, startedAt);
    },
    take(requestId) {
      const startedAt = startTimes.get(requestId);
      startTimes.delete(requestId);
      return startedAt;
    },
    clear() {
      startTimes.clear();
    },
    get size() {
      return startTimes.size;
    },
  };
}
